import { useEventListener, useLocalStorage, useSessionStorage } from '@vueuse/core'
import { i18n } from 'boot/i18n'
import { useAuth } from 'feathers-pinia'
import { jwtDecode } from 'jwt-decode'
import { DateTime } from 'luxon'
import { acceptHMRUpdate, defineStore } from 'pinia'
import Loading from 'quasar/src/plugins/loading/Loading.js';import Notify from 'quasar/src/plugins/notify/Notify.js';
import { consumeAuthTokensFromHash, feathersClient } from 'src/feathers-client.js'
import { connectRealtime, disconnectRealtime } from 'src/realtime-client'

const accessTokenStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-jwt' : 'vision-customer-jwt'
const refreshTokenStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-refresh-jwt' : 'vision-customer-refresh-jwt'
const loginRedirectStorageKey = process.env.FRONTEND_TYPE === 'Admin' ? 'vision-admin-login-redirect' : 'vision-customer-login-redirect'
const authStateStorageKeys = new Set([accessTokenStorageKey, refreshTokenStorageKey, 'isLoggedIn', 'selectedCustomer'])
const allowedMFAMethods = new Set(['authenticator', 'email', 'sms'])

const isSharingCenterMeetingPath = () => {
  if (process.env.FRONTEND_TYPE !== 'Customer' || typeof window === 'undefined') {
    return false
  }

  return /^\/meeting\/[^/]+/.test(String(window.location.pathname || ''))
}

export const useAuthStore = defineStore('auth', () => {
  const userStore = useUserStore()
  const UIStore = useUIStore()
  const authResponse = ref(null)

  const totpIdentifier = useLocalStorage('totpIdentifier', '')
  const rememberMFA = useLocalStorage('rememberMFA', false)
  const keepMeLoggedIn = useLocalStorage('keepMeLoggedIn', false)
  const mfaRememberLifetimeDays = useSessionStorage('mfaRememberLifetimeDays', 14)
  const pendingMFAMethod = useSessionStorage('pendingMFAMethod', '')
  const pendingMFAAvailableMethods = useSessionStorage('pendingMFAAvailableMethods', [])
  const pendingMFACodeSentMethods = useSessionStorage('pendingMFACodeSentMethods', [])
  const pendingMFACodeCooldownUntilByMethod = useSessionStorage('pendingMFACodeCooldownUntilByMethod', {})
  const pendingMFASetupCodeSent = useSessionStorage('pendingMFASetupCodeSent', false)
  const pendingMFASetupCodeVerified = useSessionStorage('pendingMFASetupCodeVerified', false)
  const pendingMFASetupCodeCooldownUntil = useSessionStorage('pendingMFASetupCodeCooldownUntil', 0)
  const pendingMFASetupCodeBlockedUntil = useSessionStorage('pendingMFASetupCodeBlockedUntil', 0)
  const pendingMFAStateUserId = useSessionStorage('pendingMFAStateUserId', '')
  const loginRedirect = useLocalStorage(loginRedirectStorageKey, '')
  const isLoggedIn = useLocalStorage('isLoggedIn', false)

  // For customer portal
  const selectedCustomer = useLocalStorage('selectedCustomer', null)
  const preventSelectedCustomerWatcher = ref(false)
  const isSwitchingCustomer = ref(false)
  const accessToken = computed(() => {
    return authResponse.value?.accessToken ?? null
  })
  const selectedCustomerInfo = computed(() => {
    return authResponse.value?.authentication?.payload?.customer ?? null
  })
  const availableCustomers = computed(() => {
    return authResponse.value?.authentication?.payload?.customers ?? []
  })
  const customerLogo = ref(null)

  const customerLogoMark = ref(null)
  const whitelabelConfig = ref(null)
  const whitelabelLoading = ref(false)
  const whitelabelLoaded = ref(process.env.FRONTEND_TYPE !== 'Customer')

  const api = useFeathers()
  let whitelabelResolvePromise = null

  const auth = useAuth({
    api,
    servicePath: 'users',
    onSuccess: async (result) => {
      await setAuthResponse(result, false)
    },
    onInitSuccess: async (result) => {
      await setAuthResponse(result, false)
    },
  })

  const syncPwaManifestLink = (customerContext = null) => {
    if (typeof window === 'undefined') {
      return
    }

    const syncManagedManifestLink = window.__OMNI_SYNC_PWA_MANIFEST__
    if (typeof syncManagedManifestLink !== 'function') {
      return
    }

    syncManagedManifestLink({
      customerContext: customerContext ? String(customerContext) : null,
    })
  }

  const whitelabelBrandingSettings = computed(() => {
    const branding = whitelabelConfig.value?.branding
    if (!branding || typeof branding !== 'object') {
      return []
    }

    return [
      { key: 'portal-name', value: branding.portalName },
      { key: 'primary-color', value: branding.primaryColor },
      { key: 'customer-logo', value: branding.customerLogo },
      { key: 'customer-logo-mark', value: branding.customerLogoMark },
      { key: 'support-email-address', value: branding.supportEmailAddress },
    ].filter((setting) => {
      return typeof setting.value !== 'undefined' && setting.value !== null
    })
  })

  const whitelabelPortalName = computed(() => {
    const portalName = whitelabelConfig.value?.branding?.portalName
    return typeof portalName === 'string' && portalName.trim().length > 0 ? portalName.trim() : ''
  })

  const whitelabelCustomerLogo = computed(() => {
    const logoUrl = whitelabelConfig.value?.branding?.customerLogo?.url
    return typeof logoUrl === 'string' && logoUrl.length > 0 ? logoUrl : null
  })

  const whitelabelCustomerLogoMark = computed(() => {
    const logoMarkUrl = whitelabelConfig.value?.branding?.customerLogoMark?.url
    return typeof logoMarkUrl === 'string' && logoMarkUrl.length > 0 ? logoMarkUrl : null
  })

  const resolveWhitelabelConfig = async ({ force = false } = {}) => {
    if (process.env.FRONTEND_TYPE !== 'Customer' || typeof window === 'undefined') {
      whitelabelLoaded.value = true
      return null
    }

    if (whitelabelLoaded.value && !force) {
      return whitelabelConfig.value
    }

    if (whitelabelResolvePromise && !force) {
      return whitelabelResolvePromise
    }

    whitelabelLoading.value = true
    whitelabelResolvePromise = api
      .service('whitelabels')
      .resolve(
        { hostname: window.location.hostname, force },
        {
          skipAuthHeader: true,
          skipAuthRefresh: true,
          skipAuthRedirect: true,
        },
      )
      .then((result) => {
        whitelabelConfig.value = result || null
        return whitelabelConfig.value
      })
      .catch(() => {
        whitelabelConfig.value = null
        return null
      })
      .finally(() => {
        whitelabelLoading.value = false
        whitelabelLoaded.value = true
        whitelabelResolvePromise = null
      })

    return whitelabelResolvePromise
  }

  const pwaManifestCustomerContext = computed(() => {
    const payload = authResponse.value?.authentication?.payload
    const isCustomerUserSession = process.env.FRONTEND_TYPE === 'Customer' && payload?.mfa === true && !!payload?.customerUser?._id

    if (!isCustomerUserSession) {
      return null
    }

    return selectedCustomer.value || payload?.customer?._id || null
  })

  watch(
    () => authResponse.value,
    (newValue) => {
      userStore.user = newValue?.user
      if (process.env.FRONTEND_TYPE === 'Customer') {
        UIStore.setPresenceStatus(newValue?.user?.sharingcenterPresenceStatus || 'online')
      }
      const isMFACompleted = newValue?.authentication?.payload?.mfa === true

      if (!isMFACompleted) {
        customerLogo.value = null
        customerLogoMark.value = null
        return
      }

      customerLogo.value = newValue?.user?.resolvedSettings?.settings?.find((setting) => setting?.key === 'customer-logo')?.value?.url ?? null
      customerLogoMark.value = newValue?.user?.resolvedSettings?.settings?.find((setting) => setting?.key === 'customer-logo-mark')?.value?.url ?? null
    },
    { deep: true },
  )

  watch(
    () => pwaManifestCustomerContext.value,
    (customerContext) => {
      syncPwaManifestLink(customerContext)
    },
  )

  const parsePendingUntilMillis = (value, nowMillis = Date.now()) => {
    if (!Number.isFinite(value)) {
      return 0
    }

    const normalizedMillis = value > 10_000_000_000 ? value : value * 1000
    return normalizedMillis > nowMillis ? normalizedMillis : 0
  }

  const extractAccessTokenFromAuthResponse = (response) => {
    const accessTokenCandidate = response?.accessToken
    if (typeof accessTokenCandidate !== 'string' || accessTokenCandidate.length === 0) {
      return null
    }
    return accessTokenCandidate
  }

  const sanitizePendingMFACodeCooldownByMethod = (value, nowMillis = Date.now()) => {
    if (!value || typeof value !== 'object') {
      return {}
    }

    return Object.entries(value).reduce((result, [method, untilValue]) => {
      if (!allowedMFAMethods.has(method)) {
        return result
      }

      const untilMillis = parsePendingUntilMillis(Number(untilValue), nowMillis)
      if (untilMillis > nowMillis) {
        result[method] = untilMillis
      }
      return result
    }, {})
  }

  const extractPendingMFAState = (response) => {
    const responsePendingMFA = response?.pendingMFA && typeof response.pendingMFA === 'object' ? response.pendingMFA : {}
    const payloadPendingMFA = response?.authentication?.payload && typeof response.authentication.payload === 'object' ? response.authentication.payload : {}

    return {
      codeSentMethods: responsePendingMFA.codeSentMethods ?? payloadPendingMFA.pendingMFACodeSentMethods,
      codeCooldownUntilByMethod:
        responsePendingMFA.codeCooldownUntilByMethod ?? responsePendingMFA.cooldownUntilByMethod ?? payloadPendingMFA.pendingMFACodeCooldownUntilByMethod,
      setupCodeSent: responsePendingMFA.setupCodeSent ?? payloadPendingMFA.pendingMFASetupCodeSent,
      setupCodeVerified: responsePendingMFA.setupCodeVerified ?? payloadPendingMFA.pendingMFASetupCodeVerified,
      setupCodeCooldownUntil: responsePendingMFA.setupCodeCooldownUntil ?? payloadPendingMFA.pendingMFASetupCodeCooldownUntil,
      setupCodeBlockedUntil: responsePendingMFA.setupCodeBlockedUntil ?? payloadPendingMFA.pendingMFASetupCodeBlockedUntil,
    }
  }

  const clearPendingMFAState = () => {
    pendingMFAMethod.value = ''
    pendingMFAAvailableMethods.value = []
    pendingMFACodeSentMethods.value = []
    pendingMFACodeCooldownUntilByMethod.value = {}
    pendingMFASetupCodeSent.value = false
    pendingMFASetupCodeVerified.value = false
    pendingMFASetupCodeCooldownUntil.value = 0
    pendingMFASetupCodeBlockedUntil.value = 0
    pendingMFAStateUserId.value = ''
  }

  const syncPendingMFAState = (userId, response = null) => {
    const normalizedUserId = userId ? String(userId) : ''
    const nowMillis = Date.now()
    const isSamePendingUser = normalizedUserId !== '' && String(pendingMFAStateUserId.value || '') === normalizedUserId

    if (!isSamePendingUser) {
      pendingMFACodeSentMethods.value = []
      pendingMFACodeCooldownUntilByMethod.value = {}
      pendingMFASetupCodeSent.value = false
      pendingMFASetupCodeVerified.value = false
      pendingMFASetupCodeCooldownUntil.value = 0
      pendingMFASetupCodeBlockedUntil.value = 0
    }

    pendingMFACodeCooldownUntilByMethod.value = sanitizePendingMFACodeCooldownByMethod(pendingMFACodeCooldownUntilByMethod.value, nowMillis)
    pendingMFASetupCodeCooldownUntil.value = parsePendingUntilMillis(Number(pendingMFASetupCodeCooldownUntil.value), nowMillis)
    pendingMFASetupCodeBlockedUntil.value = parsePendingUntilMillis(Number(pendingMFASetupCodeBlockedUntil.value), nowMillis)

    if (response) {
      const pendingMFAState = extractPendingMFAState(response)

      if (Array.isArray(pendingMFAState.codeSentMethods)) {
        pendingMFACodeSentMethods.value = [...new Set(pendingMFAState.codeSentMethods.filter((method) => allowedMFAMethods.has(method)))]
      }

      const responseCooldownMap = sanitizePendingMFACodeCooldownByMethod(pendingMFAState.codeCooldownUntilByMethod, nowMillis)
      if (Object.keys(responseCooldownMap).length > 0) {
        pendingMFACodeCooldownUntilByMethod.value = {
          ...(pendingMFACodeCooldownUntilByMethod.value || {}),
          ...responseCooldownMap,
        }
      }

      if (Object.prototype.hasOwnProperty.call(pendingMFAState, 'setupCodeSent') && typeof pendingMFAState.setupCodeSent === 'boolean') {
        pendingMFASetupCodeSent.value = pendingMFAState.setupCodeSent
      }

      if (Object.prototype.hasOwnProperty.call(pendingMFAState, 'setupCodeVerified') && typeof pendingMFAState.setupCodeVerified === 'boolean') {
        pendingMFASetupCodeVerified.value = pendingMFAState.setupCodeVerified
      }

      if (Object.prototype.hasOwnProperty.call(pendingMFAState, 'setupCodeCooldownUntil')) {
        pendingMFASetupCodeCooldownUntil.value = parsePendingUntilMillis(Number(pendingMFAState.setupCodeCooldownUntil), nowMillis)
      }

      if (Object.prototype.hasOwnProperty.call(pendingMFAState, 'setupCodeBlockedUntil')) {
        pendingMFASetupCodeBlockedUntil.value = parsePendingUntilMillis(Number(pendingMFAState.setupCodeBlockedUntil), nowMillis)
      }
    }

    pendingMFAStateUserId.value = normalizedUserId
  }

  // Functions like verifyMFA may set authResponse outside the usual auth flow
  const setAuthResponse = async (response, reAuthenticate = true) => {
    const resolvedAccessToken = extractAccessTokenFromAuthResponse(response)
    const payload = response?.authentication?.payload
    authResponse.value = response || null

    if (resolvedAccessToken) {
      await api.authentication.setAccessToken(resolvedAccessToken)
    }

    const rememberDaysFromPayload = Number(payload?.mfaRememberLifetimeDays || response?.mfaRememberLifetimeDays || 0)
    if (rememberDaysFromPayload > 0) {
      mfaRememberLifetimeDays.value = rememberDaysFromPayload
    }
    if (payload?.mfa === true) {
      clearPendingMFAState()
    }
    if (response?.refreshToken) {
      window.localStorage.setItem(refreshTokenStorageKey, response.refreshToken)
    }
    const customerId = payload?.customer?._id
    if (customerId && selectedCustomer.value !== customerId) {
      selectedCustomer.value = customerId
    }

    isLoggedIn.value = true

    if (reAuthenticate) {
      await reAuthenticateAndSync()

      return
    }

    if (payload?.mfa === true) {
      void connectRealtime(resolvedAccessToken).catch((error) => {
        console.error('Realtime connection failed', error)
      })
    } else {
      await disconnectRealtime()
    }
  }

  const reAuthenticateAndSync = async () => {
    const response = await api.reAuthenticate(true).catch(() => null)
    if (response) {
      await setAuthResponse(response, false)
    }
    return response
  }

  function clearLoginRedirect() {
    loginRedirect.value = ''
  }

  let authStateSyncInFlight = null
  let authStateSyncPending = false

  const syncLoginFromExternalContext = async () => {
    try {
      const response = await api.reAuthenticate(true).catch(() => null)
      if (!response) {
        isLoggedIn.value = false
        return
      }

      const { Router } = await import('src/router/index.js')
      const currentRoute = Router.currentRoute.value
      const currentPath = String(currentRoute?.path || '')

      const isMFACompleted = response?.authentication?.payload?.mfa === true
      if (!isMFACompleted) {
        const pendingPrimaryMethod = response.pendingMFA?.primaryMethod || response.authentication?.payload?.pendingMFAPrimaryMethod || 'authenticator'
        const shouldActivateMFA = pendingPrimaryMethod === 'authenticator' && response?.user?.isMFAConfigured !== true
        const targetRouteName = shouldActivateMFA ? 'activateMfa' : 'verifyMfa'

        if (!currentPath.startsWith('/auth') && currentRoute?.meta?.requiresAuth !== false) {
          loginRedirect.value = currentRoute?.fullPath || currentPath || '/'
        }

        if (currentRoute?.name !== targetRouteName) {
          await Router.push({ name: targetRouteName })
        }

        await setAuthResponse(response, false)
        return
      }

      await setAuthResponse(response, false)

      if (!currentPath.startsWith('/auth')) {
        return
      }

      const redirect = loginRedirect.value
      if (redirect) {
        clearLoginRedirect()
        await Router.push(redirect)
        return
      }

      if (Router.currentRoute.value?.name !== 'main') {
        await Router.push({ name: 'main' })
      }
    } catch (error) {
      isLoggedIn.value = false
    }
  }

  const syncLogoutFromExternalContext = async () => {
    const hasLocalAccessToken = !!(await api.authentication?.getAccessToken())
    const hasLocalSession = auth.isAuthenticated.value || hasLocalAccessToken || !!authResponse.value?.accessToken

    if (!hasLocalSession) {
      if (isLoggedIn.value !== false) {
        isLoggedIn.value = false
      }
      return
    }

    await fullLogout(true)
  }

  const syncAuthStateFromSharedStorage = async () => {
    const sharedAccessToken = window.localStorage.getItem(accessTokenStorageKey)
    const sharedRefreshToken = window.localStorage.getItem(refreshTokenStorageKey)
    const hasSharedSession = !!(sharedAccessToken || sharedRefreshToken)

    if (!hasSharedSession) {
      await syncLogoutFromExternalContext()
      return
    }

    const localAccessToken = authResponse.value?.accessToken || (await api.authentication?.getAccessToken())
    if (auth.isAuthenticated.value && localAccessToken && sharedAccessToken && localAccessToken === sharedAccessToken) {
      if (isLoggedIn.value !== true) {
        isLoggedIn.value = true
      }
      return
    }

    await syncLoginFromExternalContext()
  }

  const queueAuthStateSync = async () => {
    authStateSyncPending = true

    if (authStateSyncInFlight) {
      await authStateSyncInFlight
      return
    }

    authStateSyncInFlight = (async () => {
      try {
        while (authStateSyncPending) {
          authStateSyncPending = false
          await syncAuthStateFromSharedStorage()
        }
      } finally {
        authStateSyncInFlight = null
      }
    })()

    await authStateSyncInFlight
  }

  const setupAuthStateSyncListeners = () => {
    if (typeof window === 'undefined') {
      return
    }

    const syncFromHashAndStorage = async () => {
      consumeAuthTokensFromHash()
      await queueAuthStateSync()
    }

    const handleStorageEvent = (event) => {
      if (event.storageArea !== window.localStorage) {
        return
      }
      if (event.key && !authStateStorageKeys.has(event.key)) {
        return
      }

      void queueAuthStateSync()
    }

    const handleWindowFocus = () => {
      void syncFromHashAndStorage()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncFromHashAndStorage()
      }
    }

    const handlePageShow = () => {
      void syncFromHashAndStorage()
    }

    const handleOnline = () => {
      void syncFromHashAndStorage()
    }

    const handleHashChange = () => {
      void syncFromHashAndStorage()
    }

    useEventListener(window, 'storage', handleStorageEvent)
    useEventListener(window, 'focus', handleWindowFocus)
    useEventListener(window, 'pageshow', handlePageShow)
    useEventListener(window, 'online', handleOnline)
    useEventListener(window, 'hashchange', handleHashChange)
    useEventListener(document, 'visibilitychange', handleVisibilityChange)
  }

  auth.reAuthenticate()
  setupAuthStateSyncListeners()
  void queueAuthStateSync()

  watch(
    () => selectedCustomer.value,
    (newValue) => {
      if (preventSelectedCustomerWatcher.value !== true) {
        window.location.href = '/'
      }
      preventSelectedCustomerWatcher.value = false
    },
  )

  async function fullLogout(redirect = true) {
    keepMeLoggedIn.value = false
    clearPendingMFAState()
    clearLoginRedirect()
    UIStore.clearSharingCenterMeeting()
    await disconnectRealtime()
    const accessToken = await api.authentication?.getAccessToken()
    const refreshToken = window.localStorage.getItem(refreshTokenStorageKey)
    const decodedAccessToken = accessToken ? jwtDecode(accessToken) : undefined
    if (accessToken && decodedAccessToken?.exp && decodedAccessToken?.exp >= DateTime.now().toSeconds()) {
      const logoutParams = {
        skipLogoutRetry: true,
      }

      if (refreshToken) {
        logoutParams.headers = {
          'X-Refresh-Token': refreshToken,
        }
      }

      await feathersClient
        .service('authentication')
        .remove(null, logoutParams)
        .catch(() => null)
    }
    await api.authentication?.removeAccessToken()
    window.localStorage.removeItem(refreshTokenStorageKey)
    authResponse.value = null
    isLoggedIn.value = false

    if (!rememberMFA.value) {
      totpIdentifier.value = ''
    }

    if (redirect && !isSharingCenterMeetingPath()) {
      const { Router } = await import('src/router/index.js')
      await Router.push({ name: 'login' })
    }
  }

  async function switchCustomer(customerId) {
    if (isSwitchingCustomer.value || customerId === selectedCustomer.value) return
    isSwitchingCustomer.value = true
    Loading.show()
    preventSelectedCustomerWatcher.value = true
    const previousCustomerId = selectedCustomer.value
    const existingRefreshToken = String(window.localStorage.getItem(refreshTokenStorageKey) || '')
    const switchCustomerParams = existingRefreshToken
      ? {
          headers: {
            'X-Refresh-Token': existingRefreshToken,
          },
        }
      : undefined
    try {
      // Unmount cached customer pages before their reactive queries can see the new context.
      await nextTick()
      const switchCustomerPayload = {
        customer: customerId,
        ...(existingRefreshToken ? { refreshToken: existingRefreshToken } : {}),
      }
      const response = await api
        .service('users')
        .switchCustomer(switchCustomerPayload, switchCustomerParams)
        .catch((err) => {
          return false
        })

      if (!response) {
        Notify.create({
          type: 'negative',
          message: i18n.global.t('Shared.Globals.Switching customer has failed'),
          progress: true,
          html: true,
        })
        return
      }

      const requiresMFA = response.authentication?.payload?.mfa !== true

      const refreshTokenToPersist = typeof response.refreshToken === 'string' ? response.refreshToken : ''
      const persistRefreshToken = () => {
        if (refreshTokenToPersist) {
          window.localStorage.setItem(refreshTokenStorageKey, refreshTokenToPersist)
          return
        }
        window.localStorage.removeItem(refreshTokenStorageKey)
      }

      const rememberDaysFromPayload = Number(response?.authentication?.payload?.mfaRememberLifetimeDays || response?.mfaRememberLifetimeDays || 0)
      if (rememberDaysFromPayload > 0) {
        mfaRememberLifetimeDays.value = rememberDaysFromPayload
      }

      const switchedAccessToken = extractAccessTokenFromAuthResponse(response)
      if (!switchedAccessToken) {
        throw new Error('Switch customer did not return a valid access token')
      }

      UIStore.clearSharingCenterMeeting()
      await disconnectRealtime()

      // Set the new access token in the store
      await api.authentication.setAccessToken(switchedAccessToken)
      persistRefreshToken()

      if (requiresMFA) {
        const previousAuthentication = authResponse.value?.authentication
        authResponse.value = {
          ...(authResponse.value || {}),
          accessToken: switchedAccessToken,
          refreshToken: refreshTokenToPersist || null,
          authentication: {
            ...(previousAuthentication || {}),
            accessToken: switchedAccessToken,
            payload: {
              ...(previousAuthentication?.payload || {}),
              pendingMFAPrimaryMethod: response.pendingMFA?.primaryMethod || response.authentication?.payload?.pendingMFAPrimaryMethod || 'authenticator',
              pendingMFAFallbackMethods: response.pendingMFA?.fallbackMethods || response.authentication?.payload?.pendingMFAFallbackMethods || [],
              mfa: false,
            },
          },
          user: response.user || authResponse.value?.user || userStore.user,
        }

        const pendingPrimaryMethod = response.pendingMFA?.primaryMethod || response.authentication?.payload?.pendingMFAPrimaryMethod || 'authenticator'
        const pendingFallbackMethods = response.pendingMFA?.fallbackMethods || response.authentication?.payload?.pendingMFAFallbackMethods

        pendingMFAMethod.value = pendingPrimaryMethod
        pendingMFAAvailableMethods.value = [pendingPrimaryMethod, ...(Array.isArray(pendingFallbackMethods) ? pendingFallbackMethods : [])].filter(
          (method, index, methods) => {
            return ['authenticator', 'email', 'sms'].includes(method) && methods.indexOf(method) === index
          },
        )
        syncPendingMFAState(response.user?._id || authResponse.value?.user?._id || userStore.user?._id, response)

        const { Router } = await import('src/router/index.js')
        if (pendingPrimaryMethod === 'authenticator' && !userStore.user?.isMFAConfigured) {
          await Router.push({ name: 'activateMfa' })
        } else {
          await Router.push({ name: 'verifyMfa' })
        }
        return
      }

      // Force a re-authenticate, otherwise Feathers will not re-pickup the newly set token
      // Set selected customer before authenticate resolves so follow-up requests don't use stale customer context.
      selectedCustomer.value = customerId
      await auth.authenticate({
        strategy: 'jwt',
        accessToken: switchedAccessToken,
      })
      void connectRealtime(switchedAccessToken).catch((error) => {
        console.error('Realtime connection failed', error)
      })
      persistRefreshToken()
      if (authResponse.value) {
        authResponse.value = {
          ...authResponse.value,
          refreshToken: refreshTokenToPersist || null,
        }
      }

      clearPendingMFAState()
      const { Router } = await import('src/router/index.js')
      const routeName = String(Router.currentRoute.value.name || '')
      if (routeName.startsWith('sc-')) {
        UIStore.resetLastSharingCenterRoute()
        await Router.replace({ name: hasAssignedLicense('OMNI-SESSIONS') ? 'sc-overview' : 'main' })
      } else if (['statistics-meetings', 'statistics-meetings-detail'].includes(routeName) && !hasAssignedLicense('OMNI-SESSIONS')) {
        await Router.replace({ name: 'statistics-overview' })
      }
    } catch (err) {
      if (selectedCustomer.value !== previousCustomerId) {
        preventSelectedCustomerWatcher.value = true
        selectedCustomer.value = previousCustomerId
      }

      Notify.create({
        type: 'negative',
        message: i18n.global.t('Shared.Globals.Switching customer has failed'),
        progress: true,
        html: true,
      })
    } finally {
      isSwitchingCustomer.value = false
      Loading.hide()
    }
  }

  function isGlobalAdmin() {
    return authResponse.value?.user?.servicePermissions?.users?.includes('global_admin')
  }

  function hasServicePermission(service, allowed = []) {
    // Always allow global_admin to anything, if present at the users service
    if (isGlobalAdmin()) {
      return true
    }

    const servicePermissionsList = authResponse.value?.user?.servicePermissions
    if (!servicePermissionsList || !servicePermissionsList[service]) {
      return false
    }

    if (typeof allowed === 'string') {
      allowed = [allowed]
    }

    return servicePermissionsList[service].some((permission) => {
      return allowed.includes(permission)
    })
  }

  function hasObjectPermission(object, allowed = []) {
    if (isGlobalAdmin()) {
      return true
    }

    if (!object?.objectPermissions) {
      return false
    }

    return object.objectPermissions.some((permission) => allowed.includes(permission))
  }

  function hasAssignedLicense(productNamePrefix) {
    const prefix = typeof productNamePrefix === 'string' ? productNamePrefix.trim() : ''
    const assignedLicenses = Array.isArray(authResponse.value?.user?.assignedLicenses) ? authResponse.value.user.assignedLicenses : []

    return prefix.length > 0 && assignedLicenses.some((license) => String(license?.productName || '').startsWith(prefix))
  }

  return {
    selectedCustomer,
    isSwitchingCustomer,
    selectedCustomerInfo,
    availableCustomers,
    preventSelectedCustomerWatcher,
    customerLogo,
    customerLogoMark,
    whitelabelConfig,
    whitelabelLoading,
    whitelabelLoaded,
    whitelabelBrandingSettings,
    whitelabelPortalName,
    whitelabelCustomerLogo,
    whitelabelCustomerLogoMark,
    resolveWhitelabelConfig,
    switchCustomer,
    hasServicePermission,
    hasObjectPermission,
    hasAssignedLicense,
    isGlobalAdmin,
    authResponse,
    ...auth,
    totpIdentifier,
    rememberMFA,
    keepMeLoggedIn,
    pendingMFAMethod,
    pendingMFAAvailableMethods,
    pendingMFACodeSentMethods,
    pendingMFACodeCooldownUntilByMethod,
    pendingMFASetupCodeSent,
    pendingMFASetupCodeVerified,
    pendingMFASetupCodeCooldownUntil,
    pendingMFASetupCodeBlockedUntil,
    pendingMFAStateUserId,
    mfaRememberLifetimeDays,
    fullLogout,
    setAuthResponse,
    reAuthenticateAndSync,
    clearPendingMFAState,
    syncPendingMFAState,
    accessToken,
    loginRedirect,
    clearLoginRedirect,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
